import ColorPicker from "components/basic/color-picker/ColorPicker";
import {
    IDatasetSetpointConfig,
    IDatasetSetPointValidationProps,
    IDatasetSetpointValueProp,
    ValidationDifference,
} from "components/dashboard/dataset-setpoint-table/common/api/interfaces/interfaces";
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import React from "react";
import { T } from "services/lang/LanguageService";

interface IValuesValidationEditorProps {
    selectedValue: IDatasetSetpointValueProp;
    currentConfig: IDatasetSetpointConfig;
    setCurrentConfig: (config: IDatasetSetpointConfig) => void;
}

const DIFFERENCE_OPTIONS = [
    {label: T("word.percent"), value: "PERCENT"},
    {label: T("word.absolute"), value: "ABS"},
];


const ValuesValidationEditor = ({selectedValue, setCurrentConfig, currentConfig}: IValuesValidationEditorProps) => {
    const currentValidation = structuredClone(currentConfig).values.find(
        (value) => value.dataset === selectedValue.dataset && value.setpointDataset === selectedValue.setpointDataset
    )?.validation;
    const indexedValidation = currentValidation?.map((item, index) => ({...item, index}));

    const onRowEditComplete = (e: any) => {
        const {newData, index} = e;
        delete newData.index;
        const tempConfig = structuredClone(currentConfig);
        const datasetToUpdate = tempConfig.values.find(
            (value) =>
                value.dataset === selectedValue.dataset && value.setpointDataset === selectedValue.setpointDataset
        );
        datasetToUpdate!.validation!.forEach((validation, i) => {
            if (i === index) {
                datasetToUpdate!.validation![i] = newData;
            } else {
                validation.difference = newData.difference;
            }
        });
        setCurrentConfig(tempConfig);
    };

    const highlightEditor = (options: any) => {
        return (
            <div className="flex justify-content-center align-items-center">
                <ColorPicker
                    value={options.rowData.highlightColor?.color}
                    onValueChange={(e) =>
                        options.editorCallback({
                            color: `${e.target.value}`,
                            bgColor: options.rowData.highlightColor?.bgColor,
                        })
                    }
                />
                <ColorPicker
                    value={options.rowData.highlightColor?.bgColor}
                    onValueChange={(e) =>
                        options.editorCallback({
                            color: options.rowData.highlightColor?.color,
                            bgColor: `${e.target.value}`,
                        })
                    }
                />
                <i
                    className="pi pi-refresh ml-2 cursor-pointer"
                    onClick={() =>
                        options.editorCallback({
                            color: "#000000",
                            bgColor: "#FFFFFF",
                        })
                    }
                />
            </div>
        );
    };

    const numberEditor = (options: any) => {
        return (
            <InputNumber
                value={options.value}
                onValueChange={(e) => options.editorCallback(e.target.value)}
                style={{width: "100%"}}
            />
        );
    };

    const differenceDropdownEditor = (options: any) => {
        return (
            <Dropdown
                value={options.value}
                options={DIFFERENCE_OPTIONS}
                onChange={(e) => options.editorCallback(e.value)}
            />
        );
    };

    const highlightBodyTemplate = (rowData: any) => {
        let tempColor = rowData.highlightColor ? rowData.highlightColor?.color : "#000000";
        let tempBgColor = rowData.highlightColor ? rowData.highlightColor?.bgColor : "#FFFFFF";
        tempColor = tempColor.startsWith("#") ? tempColor : `#${  tempColor}`;
        tempBgColor = tempBgColor.startsWith("#") ? tempBgColor : `#${  tempBgColor}`;
        return (
            <span className="p-1 pl-2 pr-2" style={{color: tempColor, backgroundColor: tempBgColor}}>
                {T("word.text")}
            </span>
        );
    };

    const deleteValidationButtonBodyTemplate = (rowData: any) => {
        return (
            <i
                className="pi pi-trash text-red-500 cursor-pointer"
                onClick={() => {
                    const tempConfig = structuredClone(currentConfig);
                    const datasetToUpdate = tempConfig.values.find(
                        (value) =>
                            value.dataset === selectedValue.dataset &&
                            value.setpointDataset === selectedValue.setpointDataset
                    );
                    datasetToUpdate!.validation!.splice(rowData.index, 1);
                    setCurrentConfig(tempConfig);
                }}
            />
        );
    };

    const handleAddValidation = () => {
        const tempConfig = structuredClone(currentConfig);
        const datasetToUpdate = tempConfig.values.find(
            (value) =>
                value.dataset === selectedValue.dataset &&
                value.setpointDataset === selectedValue.setpointDataset
        );
        let newDifference = ValidationDifference.PERCENT;
        if (datasetToUpdate!.validation && datasetToUpdate!.validation.length > 0) {
            newDifference = datasetToUpdate!.validation[0].difference;
        }
        const newValidation: IDatasetSetPointValidationProps = {
            difference: newDifference,
            diff: 0,
            highlightColor: {color: "#000000", bgColor: "#FFFFFF"},
        };
        if (datasetToUpdate!.validation === undefined) {
            datasetToUpdate!.validation = [];
        }
        datasetToUpdate!.validation.push(newValidation);
        setCurrentConfig(tempConfig);
    };

    const tableHeader = (
        <div className="flex justify-content-end">
            <Button
                label={T("action.add-validation")}
                icon="pi pi-plus"
                className="dashboard-editor-button icon-green"
                onClick={handleAddValidation}
            />
        </div>
    );

    return (
        <DataTable
            value={indexedValidation}
            className="p-datatable-sm"
            editMode="row"
            size="small"
            onRowEditComplete={onRowEditComplete}
            header={tableHeader}
        >
            <Column
                field="difference"
                header={T("word.difference")}
                align="center"
                editor={(options) => differenceDropdownEditor(options)}
                style={{width: "20%"}}
                body={(rowData: any) => {
                    return rowData.difference === ValidationDifference.PERCENT ? T("word.percent"): T("word.absolute");
                }
                }
            />
            <Column
                field="diff"
                header="Diff"
                align="center"
                style={{width: "5%"}}
                editor={(options) => numberEditor(options)}
            />
            <Column
                field="highlightColor"
                header={T("word.setpoint-table.highlight-color")}
                editor={(options) => highlightEditor(options)}
                body={highlightBodyTemplate}
                align="center"
                style={{width: "55%"}}
            />
            <Column rowEditor align="center" style={{width: "15%"}}/>
            <Column body={deleteValidationButtonBodyTemplate} align="center" style={{width: "5%"}}/>
        </DataTable>
    );
};

export default ValuesValidationEditor;
